import type { JSX, ReactNode } from 'react';

type BackgroundVariant = 'default' | 'light' | 'dark';
type PaddingSize = 'none' | 'small' | 'medium' | 'large';
type MaxWidth = 'full' | 'narrow' | 'medium' | 'wide';

interface SectionProps {
  /** Background color variant */
  background?: BackgroundVariant;
  /** Vertical padding size */
  paddingY?: PaddingSize;
  /** Content max width */
  maxWidth?: MaxWidth;
  /** Section children */
  children: ReactNode;
  /** Additional CSS classes for the outer section element */
  className?: string;
  /** Additional CSS classes for the inner content wrapper */
  contentClassName?: string;
}

export const Section = ({
  background = 'default',
  paddingY = 'medium',
  maxWidth = 'full',
  children,
  className = '',
  contentClassName = '',
}: SectionProps): JSX.Element => {
  const backgroundClass: Record<BackgroundVariant, string> = {
    default: 'section-bg-default',
    light: 'section-bg-light',
    dark: 'section-bg-dark',
  };

  const paddingClass: Record<PaddingSize, string> = {
    none: 'section-py-none',
    small: 'section-py-small',
    medium: 'section-py-medium',
    large: 'section-py-large',
  };

  const maxWidthClass: Record<MaxWidth, string> = {
    full: 'section-max-full',
    narrow: 'section-max-narrow',
    medium: 'section-max-medium',
    wide: 'section-max-wide',
  };

  return (
    <section className={`section ${backgroundClass[background]} ${paddingClass[paddingY]} ${className}`.trim()}>
      <div className={`section-content ${maxWidthClass[maxWidth]} ${contentClassName}`.trim()}>{children}</div>
    </section>
  );
};
